import { isValidEmail } from '@choco-admin/utils';
import { Divider } from '@heroui/react';
import React from 'react';
import { useFormContext } from 'react-hook-form';

import {
  ChocoCheckbox,
  Input,
} from '../../../../../../../components/FormComponents';
import { parseEmailList } from '../../../../../utils/buildIntegrationPayload';
import { type SupplierFormData } from '../../../types';

export const EmailNotifications: React.FC = () => {
  const {
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<SupplierFormData>();

  // Attachments ride on the order email, so they need a recipient that can
  // actually receive one. Nonblank was not enough: `not-an-email` unlocked the
  // checkboxes and was stored, and nothing downstream rejects it, so the order
  // mail had no valid destination and nothing said so.
  const orderRecipients = parseEmailList(watch('notificationOrderEmails'));
  const canAttach =
    orderRecipients.length > 0 && orderRecipients.every(isValidEmail);

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold">Email Notifications</h3>

      <div className="space-y-4">
        <Input
          id="orderEmails"
          label="Order Emails"
          value={watch('notificationOrderEmails')}
          onChange={(value) =>
            setValue('notificationOrderEmails', value, { shouldDirty: true })
          }
          errorMessage={errors.notificationOrderEmails?.message}
          isInvalid={!!errors.notificationOrderEmails}
          className="max-w-md"
        />

        <Input
          id="messageEmails"
          label="Message Emails"
          value={watch('notificationSystemAlertEmails')}
          onChange={(value) =>
            setValue('notificationSystemAlertEmails', value, {
              shouldDirty: true,
            })
          }
          errorMessage={errors.notificationSystemAlertEmails?.message}
          isInvalid={!!errors.notificationSystemAlertEmails}
          className="max-w-md"
        />

        <Input
          id="complaintEmails"
          label="Complaint Emails"
          value={watch('notificationComplaintEmails')}
          onChange={(value) =>
            setValue('notificationComplaintEmails', value, {
              shouldDirty: true,
            })
          }
          errorMessage={errors.notificationComplaintEmails?.message}
          isInvalid={!!errors.notificationComplaintEmails}
          className="max-w-md"
        />
      </div>

      <Divider className="my-2" />

      <div className="space-y-3">
        <div>
          <h4 className="font-medium text-foreground">Order attachments</h4>
          <p className="text-sm text-default-500">
            {canAttach
              ? 'Choose which copies of the order are attached to the order email this distributor receives. Both can be on at once.'
              : 'Add an Order Emails address to attach a copy of the order.'}
          </p>
        </div>

        <ChocoCheckbox
          isSelected={watch('notificationOrderPdf')}
          onValueChange={(value) =>
            setValue('notificationOrderPdf', value, { shouldDirty: true })
          }
          isDisabled={!canAttach}
          size="sm"
          label="Attach the order as a PDF"
        />

        <ChocoCheckbox
          isSelected={watch('notificationOrderXlsx')}
          onValueChange={(value) =>
            setValue('notificationOrderXlsx', value, { shouldDirty: true })
          }
          isDisabled={!canAttach}
          size="sm"
          label="Attach the order as an Excel file"
        />
      </div>
    </div>
  );
};
